import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { MetricKind } from '@/webapi/models';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import {
  getFormattedSingleMetric,
  getSingleMetric,
  getUplift,
  nanGuard,
} from '@/features/details/summary/util';
import { Uplift } from '@/features/details/summary/uplift-badge';
import { DefaultTypography } from '@/components/typography';
import { SummaryContext } from '@/features/details/summary/context';
import { currencySymbol } from '@/utils/currencies';
import { CONTROL } from '@/features/details/shared';
import { isDollar, isPercent } from '@/features/details/charts/chart';
import { breakpoints } from '@/components/responsive';

export function SummaryValue({
  metric,
  variant: { id, name },
}: {
  metric: MetricKind;
  variant: ExperienceVariant;
}) {
  const {
    data: summary,
    hasOneVariant,
    variants,
    primaryGoal,
  } = useContext(SummaryContext);
  let value = getFormattedSingleMetric(summary, metric, id);
  if (isPercent(metric)) {
    const percentValue = getSingleMetric(summary, metric, id) * 100;
    const boundedValue =
      metric === MetricKind.PRODUCT_DETAILS_VIEWS_RATE
        ? percentValue
        : Math.min(percentValue, 100);
    value = nanGuard(
      boundedValue.toFixed(2)?.replace(`.00`, ``)?.replace(`.01`, ``),
    );
  }

  const tooltipId = useMemo(() => `metric-${metric}-${name}`, [metric, name]);
  const upliftValue = getUplift(summary, metric, variants, id);
  const otw = getSingleMetric(summary, metric, id, `otw`);
  const isPrimaryNonControl = primaryGoal === metric && name !== CONTROL;
  return (
    <SummaryBody hasOneVariant={hasOneVariant}>
      <span>
        {isDollar(metric) ? currencySymbol() : ``}
        <span data-tip data-for={tooltipId}>
          {value}
          {isPercent(metric) ? `%` : ``}
          {primaryGoal !== metric && <MetricOtw otw={otw} />}
        </span>
        {primaryGoal !== metric && name !== CONTROL && (
          <ReactTooltip
            className="uplift-tooltip"
            place="right"
            id={tooltipId}
            arrowColor="transparent"
          >
            <Uplift value={upliftValue} show />
          </ReactTooltip>
        )}
        <Uplift value={upliftValue} show={isPrimaryNonControl} />
      </span>
    </SummaryBody>
  );
}

function MetricOtw({ otw }: { otw: number }) {
  if (!otw) return null;
  return <OtwSpan>( {otw}% )</OtwSpan>;
}

const OtwSpan = styled.span`
  color: rgb(145, 150, 159) !important;
  font-weight: 400 !important;
  font-size: 1.2rem !important;
  margin-left: 4px;
`;
export function VersionName({ v: { name } }: { v: ExperienceVariant }) {
  return (
    <SummaryBody>
      {name === CONTROL ? (
        <p
          style={{
            margin: 0,
            lineHeight: `1.6rem`,
            color: `#111111`,
            fontWeight: 700,
          }}
        >
          Control
          <br />
          <span style={{ fontSize: `10px`, color: `#91969f` }}>
            (without changes)
          </span>
        </p>
      ) : (
        <span>{name}</span>
      )}
    </SummaryBody>
  );
}

export const SummaryBody = styled(DefaultTypography)`
  && {
    span {
      position: relative;
      font-size: ${(p: { hasOneVariant: boolean }) =>
        p?.hasOneVariant ? `2.3rem` : `1.4rem`};
      color: #111111;
      font-weight: 700;
      text-decoration: none;
      user-select: none;

      ${breakpoints.down(`md`)} {
        font-size: 1.2rem;
        font-family: Inter, sans-serif;
        color: black;
      }
    }
  }
`;
